/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PROFILE_COMPLETION_CALCULATION
 * Date:         2025-01-16
 * Modified By:  Cursor
 *
 * Description:
 * Updated completion calculation to count all 12 personal profile fields individually.
 * Added conditional status text ("Well Done!" at 100%) and button display logic.
 * Added View button with navigation to review page when completion is 100%.
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/PersonalProfileBox.tsx
 * - client/src/components/Setup.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PROFILE_COMPLETE_BUTTON_NAV
 * Date:         2025-01-16
 * Modified By:  Cursor
 *
 * Description:
 * Updated Complete button to navigate to setup page with profile section parameter.
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/PersonalProfileBox.tsx
 * - client/src/components/Setup.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    DASHBOARD_PROFILE_BOXES
 * Date:         2025-12-27
 * Modified By:  Cursor
 *
 * Description:
 * Personal Profile completion box showing percentage of completed profile fields.
 * Displays completion status with color-coded percentage and action buttons.
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardProfileBoxes.tsx
 * - client/src/pages/Dashboard.tsx
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUserProfile } from "../../services/usersService";
import { User } from "../../interfaces/User";
import { useIsGuest } from "../../contexts/GuestModeContext"; // [2026-07-06] GUEST_ACCESS BR-09/10

// [2025-01-16] Completion calculation: Calculate personal profile completion based on all 12 fields
const calculatePersonalProfileCompletion = (userData: User): number => {
  const totalFields = 12;
  let populatedCount = 0;

  // Required fields
  if (userData.firstName) populatedCount++;
  if (userData.lastName) populatedCount++;
  if (userData.email) populatedCount++;

  // Additional fields
  if ((userData as any).preferredCurrency) populatedCount++;
  if (userData.jobTitle) populatedCount++;
  if (userData.tel) populatedCount++;
  if (userData.bio) populatedCount++;
  if (userData.profileImages) populatedCount++;
  if (userData.linkedIn) populatedCount++;
  if (userData.x) populatedCount++;
  if (userData.instagram) populatedCount++;
  if (userData.facebook) populatedCount++;

  return Math.round((populatedCount / totalFields) * 100);
};

// [2025-12-27] Profile boxes: Personal profile completion box component
const PersonalProfileBox: React.FC = () => {
  const navigate = useNavigate();
  const isGuest = useIsGuest(); // [2026-07-06] GUEST_ACCESS: show box as N/A + locked button
  const [completion, setCompletion] = useState(0);
  const [loading, setLoading] = useState(true);

  // [2026-02-07] style-extraction: removed isCompleteHovered state, using CSS :hover instead

  // [2025-01-16] Completion calculation: Fetch user data and calculate completion
  useEffect(() => {
    // [2026-07-06] GUEST_ACCESS: no profile for guests — skip the authed fetch entirely.
    if (isGuest) {
      setLoading(false);
      return;
    }
    const fetchUserData = async () => {
      try {
        const response = await getUserProfile();
        const userData = response.data as User;

        // [2025-01-16] Completion calculation: Use helper function to calculate percentage
        const percentage = calculatePersonalProfileCompletion(userData);
        setCompletion(percentage);
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [isGuest]);

  // [2025-12-27] Profile boxes: Get color based on completion percentage
  const getStatusColor = () => {
    if (completion >= 67) return "var(--color-status-green)";
    if (completion >= 34) return "var(--color-status-orange)";
    return "var(--color-status-red)";
  };

  // [2025-01-16] Navigation: Navigate to setup page with profile section
  const handleComplete = () => {
    navigate("/setup?section=profile");
  };

  // [2025-01-16] Navigation: Navigate to review page when completion is 100%
  const handleView = () => {
    navigate("/setup/reviewuser");
  };

  // [2026-02-07] style-extraction: moved inline styles to _profile-boxes.scss
  if (loading) {
    return (
      <div className="profile-box">
        <div className="profile-box-header">
          <div className="profile-box-icon" />
          <h4 className="profile-box-title u-H2-24">Persona Profile</h4>
        </div>
        <p className="u-P2-16 profile-box-loading-text">Loading...</p>
      </div>
    );
  }

  return (
    <div className="profile-box">
      <div className="profile-box-header">
        {/* [2026-02-06] Icon color: Orange when incomplete, Green when 100% complete */}
        {/* [2026-02-13] Maya: Figma icon - 46px with colored bg/border */}
        <img
          src={completion === 100 ? "/assets/d_personal_green.svg" : "/assets/d_personal.svg"}
          alt="My Profile"
          className={`profile-box-icon ${completion === 100 ? "profile-box-icon-green" : "profile-box-icon-orange"}`}
        />
        <h4 className="profile-box-title u-H2-24">My Profile</h4>
      </div>
      {/* [2026-02-07] style-extraction: conditional class for 100% completion layout */}
      {/* [2026-02-13] Maya: Figma alignment - completion text uses P1 18 SemiBold */}
      <div className={`profile-box-status ${completion === 100 ? "profile-box-status-complete" : ""}`}>
        {isGuest ? (
          <p className="u-P1-18-semi-bold profile-box-status-text">
            Completion:{" "}
            <span className="status-percentage" style={{ color: "var(--color-grey-1)" }}>
              N/A
            </span>
          </p>
        ) : completion === 100 ? (
          <>
            <p className="u-P1-18-semi-bold profile-box-status-text">
              Completion:{" "}
              <span className="status-percentage" style={{ color: "var(--color-status-green)" }}>
                100%
              </span>
            </p>
            <p className="u-P1-18-semi-bold profile-box-status-text">
              <span className="status-percentage" style={{ color: "var(--color-status-green)" }}>
                Well Done!
              </span>
            </p>
          </>
        ) : (
          <p className="u-P1-18-semi-bold profile-box-status-text">
            Completion:{" "}
            <span className="status-percentage" style={{ color: getStatusColor() }}>
              {completion}%
            </span>
          </p>
        )}
      </div>
      <div className="profile-box-actions profile-box-actions-wrapper">
        {/* [2026-07-06] GUEST_ACCESS: guests see the Complete button but it is locked. */}
        {isGuest ? (
          <button
            type="button"
            className="u-P2-16-medium btn-profile-complete-action"
            disabled
            aria-disabled="true"
            title="Sign in to complete your profile"
            style={{ opacity: 0.5, cursor: "not-allowed" }}
          >
            Complete
          </button>
        ) : completion === 100 ? (
          <button
            type="button"
            className="link-profile-view u-P2-16-medium"
            onClick={handleView}
            aria-label="View personal profile"
          >
            View
            <svg
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="profile-box-arrow"
            >
              <path
                d="M1 1L6 6L1 11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            className="u-P2-16-medium btn-profile-complete-action"
            onClick={handleComplete}
            aria-label="Complete personal profile"
          >
            Complete
          </button>
        )}
      </div>
    </div>
  );
};

export default PersonalProfileBox;
